import { Alert, AlertDescription, AlertTitle } from '@tempo-io/sui-alert';
import { Skeleton } from '@tempo-io/sui-skeleton';
import { useTranslation } from 'react-i18next';
import { DateFormat, formatLocalDateWithFallback } from '@/common/utils/dateFormats';
import { useScheduleBarData } from '@/features/item-panel/components/hooks/useScheduleBarData';
import { DeadlineRiskMessage } from '@/features/planner/components/common/DeadlineRiskMessage';
import {
  getGoverningDeadline,
  getIsDeadlineAtRisk,
} from '@/features/planner/components/common/lib/getIsAtRisk';
import { TooltipScheduleBar } from '@/features/planner/components/common/TooltipScheduleBar';
import type { TimelineScenarioContext } from './useTimelineTableColumns';

const formatDate = (dateString: string | null | undefined, fallback: string) =>
  formatLocalDateWithFallback({
    dateString,
    formatStr: DateFormat.MONTH_SHORT_DAY_SHORT_YEAR,
    fallback,
  });

const DateFieldSkeleton = ({ alignEnd }: { alignEnd?: boolean }) => (
  <div className={`flex flex-col gap-1 ${alignEnd ? 'items-end' : ''}`}>
    <Skeleton className="h-4 w-24" />
    <Skeleton className="h-4 w-20" />
  </div>
);

export const TimelineItemTooltipScheduleBar = ({
  itemId,
  scenarioContext,
  barColor,
  barTextColor,
}: {
  itemId: number;
  scenarioContext: TimelineScenarioContext;
  barColor?: string;
  barTextColor?: string;
}) => {
  const { t } = useTranslation();
  const {
    forecastedStart,
    forecastedFinish,
    latestFinish,
    deadline,
    ancestorDeadline,
    ancestorDeadlineSourceItemName,
    isLoading,
  } = useScheduleBarData(itemId, scenarioContext);

  const fallback = t(($) => $.common.notAvailable);
  const governingDeadline = getGoverningDeadline({
    ownDeadline: deadline,
    ancestorDeadline,
    ancestorSourceItemName: ancestorDeadlineSourceItemName,
  });
  const isAtRisk = getIsDeadlineAtRisk({ forecastedFinish, deadline: governingDeadline.deadline });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 px-4 py-3">
        <Skeleton className="h-7 w-full rounded-lg" />
        <div className="flex justify-between gap-4">
          <DateFieldSkeleton />
          <DateFieldSkeleton alignEnd />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 px-4 py-3">
      {forecastedStart && forecastedFinish && (
        <TooltipScheduleBar
          forecastedStart={forecastedStart}
          forecastedFinish={forecastedFinish}
          latestFinish={latestFinish}
          isAtRisk={isAtRisk}
          barColor={barColor}
          barTextColor={barTextColor}
        />
      )}
      <div className="flex justify-between gap-4">
        <div className="flex flex-col">
          <span>{t(($) => $.loopVisualization.timelineTooltip.forecastedStart)}:</span>
          <span>{formatDate(forecastedStart, fallback)}</span>
        </div>
        <div className="flex flex-col items-end">
          <span>{t(($) => $.loopVisualization.timelineTooltip.forecastedFinish)}:</span>
          <span className={isAtRisk ? 'font-bold text-text-error' : undefined}>
            {formatDate(forecastedFinish, fallback)}
          </span>
        </div>
      </div>
      <div className="flex flex-col items-end">
        <span>{t(($) => $.loopVisualization.timelineTooltip.latestFinish)}:</span>
        <span>{formatDate(latestFinish, fallback)}</span>
      </div>
      {deadline && (
        <>
          <div className="border-b border-border-neutral" />
          <div className="flex flex-col">
            <span>{t(($) => $.loopVisualization.timelineTooltip.deadline)}:</span>
            <span>{formatDate(deadline, fallback)}</span>
          </div>
        </>
      )}
      {isAtRisk && (
        <Alert variant="error">
          <AlertTitle>{t(($) => $.loopVisualization.timelineTooltip.atRisk)}</AlertTitle>
          <AlertDescription>
            <DeadlineRiskMessage
              forecastedFinish={forecastedFinish}
              deadline={governingDeadline.deadline}
              sourceItemName={governingDeadline.sourceItemName}
            />
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
